/**
 * What to tell an operator when saving a customer or prospect fails.
 *
 * WHY THIS EXISTS. A salesperson reported on 2026-09-16 that he could not
 * create a customer: the form said "Failed to create customer" and nothing
 * else. crm-api had in fact answered with a precise reason — 409
 * DUPLICATE_COMPANY, "That company is already in the system" — because
 * "Vanderbilt Health" was already on another salesperson's books. The modal's
 * catch block threw the whole response away and printed a fixed sentence, so
 * every failure, from a validation error to a duplicate, read the same and
 * told him nothing he could act on.
 *
 * THE DUPLICATE CASE IS THE ONE THAT NEEDS WORDS, and there are three of them.
 * A `sales_manager` sees only its OWN records, so the colliding company is
 * usually invisible to the person being refused — "it already exists" alone
 * reads as a lie when the CRM shows them no such company. crm-api therefore
 * answers whether THIS caller could open the record it collided with
 * (`existing.visible`, decided by the same row filter the listings use), and
 * each answer gets its own instruction:
 *
 *   visible        → open it instead of making a second one;
 *   not visible    → it exists; ask a system administrator to assign you to it
 *                    (the CEO's own wording — the salesperson needs the fact
 *                    and the next step, not an explanation of row scoping);
 *   not disclosed  → the reseller wall withheld the record entirely, so say
 *                    only that the name is taken and who can sort it out —
 *                    XOP support, because a reseller has no system admin here.
 *
 * EVERY OTHER FAILURE GETS AS MUCH TRUE DETAIL AS THERE IS, WITH ONE
 * EXCEPTION. A validation refusal lists ALL of the server's complaints, not
 * just the first, because fixing one and pressing save again to discover the
 * next is the slowest possible way to fill a form. A permission refusal, a
 * missing record and an expired session each say what happened and what to do.
 *
 * The exception is a SYSTEMIC failure — a 5xx, or no response at all. Those
 * carry no advice for the operator and their bodies carry things an operator
 * must never be shown: crm-api answers 500 with a full Rails backtrace, and
 * printing that into a toast would put file paths and SQL on a salesperson's
 * screen. They get one plain sentence; the raw error still reaches the console
 * for whoever is debugging.
 */
export interface CustomerSaveFailure {
  title: string
  description: string
}

interface DuplicateBody {
  code?: unknown
  error?: unknown
  existing?: { id?: unknown; type?: unknown; company_name?: unknown; visible?: unknown } | null
}

export function describeCustomerSaveFailure(
  error: unknown,
  opts: { mode: "customer" | "lead"; isEditMode: boolean; companyName?: string },
): CustomerSaveFailure {
  const noun = opts.mode === "lead" ? "prospect" : "customer"
  const verb = opts.isEditMode ? "update" : "create"
  const data = (error as { response?: { data?: DuplicateBody } } | undefined)?.response?.data

  if (data?.code === "DUPLICATE_COMPANY") {
    const named = typeof data.existing?.company_name === "string" && data.existing.company_name.trim()
      ? data.existing.company_name.trim()
      : opts.companyName?.trim()
    const kind = data.existing?.type === "Lead" ? "prospect" : data.existing?.type === "Customer" ? "customer" : null
    const subject = named ? `“${named}”` : "That company"
    const withKind = kind ? `${subject} already exists as a ${kind}.` : `${subject} already exists.`

    // The record was withheld: the reseller wall decided this caller may not be
    // told WHICH row it collided with. Say the name is taken and stop.
    if (!data.existing) {
      return {
        title: "Already in the system",
        description: `${subject} already exists in the XOP system. Contact XOP support to have it assigned to you.`,
      }
    }

    if (data.existing.visible === false) {
      // The CEO's words (2026-09-16): say it exists, and say who assigns it.
      // Anything longer is a paragraph a salesperson has to decode mid-task.
      return {
        title: "Already exists",
        description: `${withKind} Ask a system administrator to assign you to it.`,
      }
    }

    return {
      title: "Already exists",
      description: `${withKind} Open the existing record instead of creating a second one.`,
    }
  }

  const status = (error as { response?: { status?: unknown } } | undefined)?.response?.status
  const serverSaid = serverMessage(data)

  // No response at all: the request never landed (offline, DNS, timeout, a
  // proxy that dropped it). Nothing the server said exists to quote.
  if (typeof status !== "number") {
    return {
      title: "Could not reach the CRM",
      description: `The ${verb} was not sent. Check your connection and try again — nothing was saved.`,
    }
  }

  if (status >= 500) {
    return {
      title: "The CRM could not save this",
      description: `Something went wrong on our side, so the ${noun} was not ${opts.isEditMode ? "updated" : "created"}. Try again in a moment; if it keeps happening, tell a system administrator.`,
    }
  }

  if (status === 401) {
    return {
      title: "Your session has expired",
      description: `Sign in again, then ${verb} the ${noun} once more. Nothing was saved.`,
    }
  }

  if (status === 403) {
    return {
      title: "Not allowed",
      description: serverSaid ?? `You do not have permission to ${verb} a ${noun}. Ask a system administrator for access.`,
    }
  }

  if (status === 404) {
    return {
      title: "No longer there",
      description: serverSaid ?? `That ${noun} no longer exists — someone may have deleted it while this was open.`,
    }
  }

  if (status === 422) {
    // ALL of them. One at a time turns a form into twenty round trips.
    const complaints = validationMessages(data)
    if (complaints.length > 0) {
      return {
        title: complaints.length === 1 ? "Check this field" : `Check ${complaints.length} fields`,
        description: complaints.join(" "),
      }
    }
  }

  if (status === 429) {
    return {
      title: "Too many requests",
      description: "Wait a moment and try again.",
    }
  }

  return {
    title: "Error",
    description: serverSaid ?? `Failed to ${verb} ${noun}`,
  }
}

/** The server's own sentence, when it sent one. */
function serverMessage(data: DuplicateBody | undefined): string | null {
  const direct = (data as { error?: unknown; message?: unknown } | undefined)?.error
  if (typeof direct === "string" && direct.trim()) return direct.trim()

  const message = (data as { message?: unknown } | undefined)?.message
  if (typeof message === "string" && message.trim()) return message.trim()

  const all = validationMessages(data)
  return all.length > 0 ? all.join(" ") : null
}

/** Every validation complaint, each ended with a full stop so they read as
 *  sentences when joined. crm-api sends them as bare fragments. */
function validationMessages(data: unknown): string[] {
  const errors = (data as { errors?: unknown } | undefined)?.errors
  const list = Array.isArray(errors)
    ? errors
    : errors && typeof errors === "object"
      ? // Rails also answers `{ errors: { field: ["is blank"] } }`.
        Object.entries(errors as Record<string, unknown>).flatMap(([field, messages]) =>
          (Array.isArray(messages) ? messages : [messages]).map((m) => `${humanField(field)} ${String(m)}`),
        )
      : []

  return list
    .map((entry) => String(entry).trim())
    .filter((entry) => entry.length > 0)
    .map((entry) => (/[.!?]$/.test(entry) ? entry : `${entry}.`))
}

function humanField(field: string): string {
  const words = field.replace(/_/g, " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
